import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { PageFilterTypes } from "@/lib/utility/data/page-filter-types.enum";
import { IPageFilterElement, PageFilterElement } from "@/models/page-filter-element.entity";
import { IReport } from "@/models/report.entity";
import { IUser } from "@/models/user.entity";
import { IVehicle } from "@/models/vehicle.entity";
import { MrfiktivReportControllerFindAllParamsGen } from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Module, getModule } from "vuex-module-decorators";
import { CoverageTypeEnum } from "../enum/coverage-type.enum";
import { DamageTypeEnum } from "../enum/damage-type.enum";
import { ProgressStatusEnum } from "../enum/partner/progress.status.enum";
import { PaginatedBaseStore } from "../paginated-base.store";
import { ReportDataAccessLayer } from "./access-layers/report.access-layer";
import { PaginationFilterListElement } from "./base-pagination.store";
import { ReportPageDataProvider } from "./page-data-provider/report.page-data-provider";
import { PartnerUserModule } from "./partner-user.store";
import { VehicleModule } from "./vehicle.store";
import { RepairStatusEnum } from "@/lib/enum/repair.enum";
import VueI18n from "@/plugins/I18nPlugin";
import { ReportRoleEnum } from "@/lib/enum/ReportRole.enum";
import { BackendResourceEnum } from "../enum/authResourceEnum";
import { ICompany } from "@/models/company.entity";
import { IReportGroup } from "@/models/report-group.entity";

const getCompanyModule = () => require("./company.store").CompanyModule;
const getReportGroupModule = () => require("./report-group.store").ReportGroupModule;

@Module({
  dynamic: true,
  namespaced: true,
  name: "reportPagination",
  store
})
export class ReportPaginationStore extends PaginatedBaseStore<IReport, MrfiktivReportControllerFindAllParamsGen> {
  _data = ReportDataAccessLayer;
  _pageProvider = ReportPageDataProvider;
  _pager = new PageDataHandler(this._data, this._pageProvider);
  protected _isLoadAll = false;

  filters: IPageFilterElement[] = [
    new PageFilterElement({ key: "progressStatus", value: ProgressStatusEnum.FINISHED, operation: "$ne" })
  ];

  hiddenFilter: IPageFilterElement[] = [
    new PageFilterElement({ key: "progressStatus", value: ProgressStatusEnum.DELETED, operation: "$ne" })
  ];

  filterOptions: PaginationFilterListElement[] = [
    { key: "claimNumber", type: PageFilterTypes.STRING, displayName: "objects.reportControl.id" },
    { key: "timestamps.created", type: PageFilterTypes.DATE, displayName: "objects.timestamp.created" },
    { key: "numberplate", type: PageFilterTypes.STRING, displayName: "objects.report.numberplate" },
    { key: "title", type: PageFilterTypes.STRING, displayName: "objects.report.title" },
    { key: "externalId", type: PageFilterTypes.STRING, displayName: "objects.report.externalId" },
    {
      key: "groupId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.groupId",
      config: {
        itemCallback: () => getReportGroupModule().entities ?? [],
        mapItemToComponent: (item: IReportGroup) => ({ item }),
        itemValue: "id",
        component: "refs-report-group",
        refType: BackendResourceEnum.REPORT_GROUP
      }
    },
    {
      key: "progressStatus",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(ProgressStatusEnum)
          .filter((v) => v !== ProgressStatusEnum.DELETED)
          .map((reportStatus) => {
            return {
              text: `partner.common.reportProgressStatus.${reportStatus}`,
              value: reportStatus
            } as IVSelectItem;
          }),
        itemValue: "value"
      },
      displayName: "objects.report.progressStatus"
    },
    { key: "status", type: PageFilterTypes.STRING, displayName: "objects.report.status" },
    { key: "isFixed", type: PageFilterTypes.BOOLEAN, displayName: "objects.report.isFixed" },
    { key: "isDrivable", type: PageFilterTypes.BOOLEAN, displayName: "objects.report.isDrivable" },
    { key: "isRoadworthy", type: PageFilterTypes.BOOLEAN, displayName: "objects.report.isRoadworthy" },
    { key: "isWrapped", type: PageFilterTypes.BOOLEAN, displayName: "objects.report.isWrapped" },
    { key: "isWrapDamaged", type: PageFilterTypes.BOOLEAN, displayName: "objects.report.isWrapDamaged" },
    {
      key: "coverageType",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(CoverageTypeEnum).map((type) => {
          return {
            text: `enums.CoverageTypeEnum.${type}`,
            value: type
          } as IVSelectItem;
        }),
        itemValue: "value"
      },
      displayName: "objects.report.coverageType"
    },
    {
      key: "damageType",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(DamageTypeEnum).map((type) => {
          return {
            text: `enums.DamageTypeEnum.${type}`,
            value: type
          } as IVSelectItem;
        }),
        itemValue: "value"
      },
      displayName: "objects.report.damageType"
    },
    {
      key: "repair.status",
      displayName: "objects.report.repair.status",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(RepairStatusEnum).map((type) => {
          return {
            text: `enums.RepairStatusEnum.${type}`,
            value: type
          } as IVSelectItem;
        }),
        itemValue: "value"
      }
    },
    { key: "repair.startDate", type: PageFilterTypes.DATE, displayName: "objects.report.repair.startDate" },
    { key: "repair.endDate", type: PageFilterTypes.DATE, displayName: "objects.report.repair.endDate" },
    { key: "customerName", type: PageFilterTypes.STRING, displayName: "objects.report.customerName" },
    { key: "firstName", type: PageFilterTypes.STRING, displayName: "common.nouns.firstName" },
    { key: "lastName", type: PageFilterTypes.STRING, displayName: "common.nouns.lastName" },
    { key: "companyName", type: PageFilterTypes.STRING, displayName: "objects.report.companyName" },
    { key: "customerContact.email", type: PageFilterTypes.STRING, displayName: "objects.report.customerContact.email" },
    { key: "customerContact.phone", type: PageFilterTypes.STRING, displayName: "objects.report.customerContact.phone" },
    {
      key: "assignees",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.assignees",
      config: {
        itemCallback: () => PartnerUserModule.entities ?? [],
        mapItemToComponent: (item: IUser) => ({ item }),
        itemValue: "id",
        component: "refs-user"
      }
    },
    {
      key: "userId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "createdBy",
      config: {
        itemCallback: () => PartnerUserModule.entities ?? [],
        mapItemToComponent: (item: IUser) => ({ item }),
        itemValue: "id",
        component: "refs-user"
      }
    },
    {
      key: "vehicleId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "common.nouns.vehicle",
      config: {
        itemCallback: () => VehicleModule?.entities ?? [],
        mapItemToComponent: (item: IVehicle) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-vehicle",
        refType: BackendResourceEnum.VEHICLE
      }
    },
    {
      key: "fleetId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "common.nouns.fleet"
    },
    { key: "customerContact.email", type: PageFilterTypes.STRING, displayName: "objects.report.customerContact.email" },
    { key: "customerContact.phone", type: PageFilterTypes.STRING, displayName: "objects.report.customerContact.phone" },
    { key: "marketingOptIn", type: PageFilterTypes.BOOLEAN, displayName: "objects.report.marketingOptIn" },
    {
      key: "registrationResults.firstregistrationDay",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.firstregistrationDay"
    },
    {
      key: "registrationResults.firstregistrationMonth",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.firstregistrationMonth"
    },
    {
      key: "registrationResults.firstregistrationYear",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.firstregistrationYear"
    },

    {
      key: "registrationResults.street",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.street"
    },
    {
      key: "registrationResults.zipCode",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.zipCode"
    },
    {
      key: "registrationResults.city",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.city"
    },
    {
      key: "registrationResults.huYear",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.huYear"
    },
    {
      key: "registrationResults.huMonth",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.huMonth"
    },
    {
      key: "registrationResults.manufacturerNameCode",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.manufacturerNameCode"
    },
    {
      key: "registrationResults.manufacturerTypeCode",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.manufacturerTypeCode"
    },
    {
      key: "registrationResults.driveTyp",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.driveTyp"
    },
    {
      key: "registrationResults.identificationnumber",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.identificationnumber"
    },
    {
      key: "registrationResults.numberplate",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.numberplate"
    },
    {
      key: "registrationResults.manufacturerName",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.manufacturerName"
    },
    {
      key: "registrationResults.manufacturerType",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.manufacturerType"
    },
    {
      key: "registrationResults.vehicleClass",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.vehicleClass"
    },
    {
      key: "registrationResults.vehicleClass",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.registrationResults.vehicleClass"
    },
    {
      key: "accidentDetails.role",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(ReportRoleEnum).map((type) => {
          return {
            text: `report.witness.roles.${type}`,
            value: type
          } as IVSelectItem;
        }),
        itemValue: "value"
      },
      displayName: "report.accidentDetails.role.label"
    },
    {
      key: "accidentDetails.date",
      type: PageFilterTypes.DATE,
      displayName: "report.accidentDetails.date"
    },
    {
      key: "broker.name",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.broker.name"
    },
    {
      key: "broker.number",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.broker.number"
    },
    {
      key: "insurances.name",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.insurances.name"
    },
    {
      key: "insurances.name",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.insurances.name"
    },
    {
      key: "insurances.number",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.insurances.number"
    },
    {
      key: "insurancs.type",
      type: PageFilterTypes.STRING,
      displayName: "objects.report.insurances.type"
    },
    { key: "message", type: PageFilterTypes.STRING, displayName: "objects.report.message" },
    { key: "datePreference", type: PageFilterTypes.STRING, displayName: "objects.report.datePreference" },
    {
      key: "companyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.companyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    {
      key: "workshopCompanyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.workshopCompanyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    {
      key: "appraiserCompanyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.appraiserCompanyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    {
      key: "insuranceCompanyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.insuranceCompanyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    {
      key: "leasingCompanyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.leasingCompanyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    {
      key: "towingCompanyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.towingCompanyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    {
      key: "attorneyCompanyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.attorneyCompanyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    {
      key: "rentalCompanyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.rentalCompanyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    {
      key: "damageBrokerCompanyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.damageBrokerCompanyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    {
      key: "insuranceBrokerCompanyId",
      type: PageFilterTypes.OBJECT_ID,
      displayName: "objects.report.insuranceBrokerCompanyId",
      config: {
        itemCallback: () => getCompanyModule()?.entities ?? [],
        mapItemToComponent: (item: ICompany) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-company",
        refType: BackendResourceEnum.COMPANY
      }
    },
    { key: "_id", type: PageFilterTypes.OBJECT_ID, displayName: "objects.report._id" },
    {
      key: "witness.firstName",
      type: PageFilterTypes.STRING,
      displayName: `${VueI18n.t("report.witness.title")} ${VueI18n.t("report.witness.firstName")}`
    },
    {
      key: "witness.lastName",
      type: PageFilterTypes.STRING,
      displayName: `${VueI18n.t("report.witness.title")} ${VueI18n.t("report.witness.lastName")}`
    },
    {
      key: "witness.contact.email",
      type: PageFilterTypes.STRING,
      displayName: `${VueI18n.t("report.witness.title")} ${VueI18n.t("report.witness.email")}`
    },
    {
      key: "witness.numberplate",
      type: PageFilterTypes.STRING,
      displayName: `${VueI18n.t("report.witness.title")} ${VueI18n.t("report.witness.numberplate")}`
    }
  ].map((f) => new PaginationFilterListElement(f));

  /**
   * The invoice balance is computed from the invoices of a report whenever the backend reads it, so it can be shown,
   * but not filtered by. Each holds one value per currency.
   */
  columnOptions: PaginationFilterListElement[] = [
    { key: "invoiceBalance.revenue", type: PageFilterTypes.NUMBER, displayName: "objects.invoiceBalanceTotal.revenue" },
    { key: "invoiceBalance.expense", type: PageFilterTypes.NUMBER, displayName: "objects.invoiceBalanceTotal.expense" },
    { key: "invoiceBalance.balance", type: PageFilterTypes.NUMBER, displayName: "objects.invoiceBalanceTotal.balance" },
    {
      key: "invoiceBalance.currency",
      type: PageFilterTypes.STRING,
      displayName: "objects.invoiceBalanceTotal.currency"
    }
  ].map((f) => new PaginationFilterListElement(f));
}

export const ReportPaginationModule = getModule(ReportPaginationStore);
